import { Empty } from 'antd';
import get from 'lodash/get';
import { Key, ReactNode, useCallback, useMemo } from 'react';
import { Loading } from '../../../components/Spin/Loading';
import { CustomColumnType } from '../../../interfaces';
import './styles.scss';

// --- PROPS DEFINITIONS ---

type CardTableProps<T extends Record<string, any>> = {
  /** Indicates if the data is currently being loaded */
  isLoading: boolean;
  /** The array of data to display in cards */
  data: readonly T[];
  /** Column definitions, which control how each field is rendered */
  columns: CustomColumnType<T>[] | undefined;
  /** Callback function when a card is clicked */
  onCardClick?: (item: T) => void;
  /** Function to get a unique key for each data item for React's reconciliation */
  rowKey?: (item: T) => Key;
};

type CardRowProps<T> = {
  /** The column definition for this row */
  column: CustomColumnType<T>;
  /** The data item for the entire card */
  item: T;
  /** The index of the data item in the main data array */
  index: number;
};

// --- CHILD COMPONENT ---

/**
 * Renders a single "row" or field within a card.
 * This component contains the display logic based on the column type.
 */
const CardRow = <T extends Record<string, any>>({
  column,
  item,
  index,
}: CardRowProps<T>) => {
  const { title, dataIndex, render, highlight, isMobileExtra, block } = column;
  const rawValue = get(item, dataIndex ?? '');

  // Determine the final content to render, handling custom renderers and default values.
  const content = useMemo((): ReactNode => {
    if (render) {
      return render(rawValue, item, index) as ReactNode;
    }
    // Fallback for non-renderable types like objects.
    return typeof rawValue === 'object' && rawValue !== null
      ? JSON.stringify(rawValue)
      : rawValue;
  }, [render, rawValue, item, index]);

  // 1. Custom rendered content (without special styling - highest priority)
  if (render && !highlight && !isMobileExtra && !block) {
    return <div className="card-custom-render">{content}</div>;
  }

  // 2. Highlighted content
  if (highlight) {
    return <div className="card-highlight">{content}</div>;
  }

  // 3. Extra content (with title)
  if (isMobileExtra) {
    return (
      <div className="card-extra">
        <span>{title as string}:</span> {content}
      </div>
    );
  }
  // 4. Normal content (title + value)
  return (
    <div className="card-normal">
      <span className="card-title">{title as string}</span>
      <div>{content}</div>
    </div>
  );
};

// --- MAIN COMPONENT ---

const CardTable = <T extends Record<string, any>>({
  isLoading,
  data,
  onCardClick,
  columns: originColumns,
  rowKey,
}: CardTableProps<T>) => {
  /**
   * Memoized categorization of columns.
   * It filters for mobile visibility and then uses a single `reduce` pass to
   * efficiently group columns into four categories for rendering.
   * This avoids repetitive filtering and keeps the grouping logic in one place.
   */
  const categorizedColumns = useMemo(() => {
    const initialCategories = {
      customRender: [] as CustomColumnType<T>[],
      highlight: [] as CustomColumnType<T>[],
      extra: [] as CustomColumnType<T>[],
      normal: [] as CustomColumnType<T>[],
    };

    if (!originColumns) return initialCategories;

    const result = originColumns
      .filter(column => column?.showInMobile)
      .reduce((acc, column) => {
        // The `block` property acts as an override, forcing the column into the "normal" group.
        if (column.block) {
          acc.normal.push(column);
        } else if (
          column.render &&
          !column.highlight &&
          !column.isMobileExtra
        ) {
          acc.customRender.push(column);
        } else if (column.highlight) {
          acc.highlight.push(column);
        } else if (column.isMobileExtra) {
          acc.extra.push(column);
        } else {
          // Default case for standard columns
          acc.normal.push(column);
        }
        return acc;
      }, initialCategories);

    const actionsIndex = result.customRender.findIndex(
      item => item.className === 'actions',
    );

    if (actionsIndex > -1) {
      const actions = result.customRender.splice(actionsIndex, 1);

      if (result.customRender.length > 0) {
        result.customRender.splice(1, 0, ...actions);
      } else {
        result.customRender = [
          { key: 'placeholder', title: '', render: () => null },
          ...actions,
        ];
      }
    }

    return result;
  }, [originColumns]);

  const handleCardClick = useCallback(
    (item: T) => {
      onCardClick?.(item);
    },
    [onCardClick],
  );

  // Use the provided rowKey function, or default to item.id or index.
  const getKey = useCallback(
    (item: T, index: number): Key => {
      if (rowKey) return rowKey(item);
      return get(item, 'id', index);
    },
    [rowKey],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="mobile-card-wrapper">
      {data.length === 0 && (
        <Empty className="center-empty" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      {data.map((item, index) => (
        <div
          key={getKey(item, index)}
          className="mobile-card"
          onClick={() => handleCardClick(item)}
        >
          {/* Group 1: Custom Rendered Columns */}
          {categorizedColumns.customRender.length > 0 && (
            <div className="group-columns">
              {categorizedColumns.customRender.map(column => (
                <CardRow
                  item={item}
                  index={index}
                  column={column}
                  key={column.key || `custom-render-${column.dataIndex}`}
                />
              ))}
            </div>
          )}

          {/* Group 2: Highlighted Columns */}
          {categorizedColumns.highlight.length > 0 && (
            <div className="group-columns">
              {categorizedColumns.highlight.map(column => (
                <CardRow
                  item={item}
                  index={index}
                  column={column}
                  key={column.key || `highlight-${column.dataIndex}`}
                />
              ))}
            </div>
          )}

          {/* Group 3: Extra Columns */}
          {categorizedColumns.extra.length > 0 && (
            <div className="group-columns">
              {categorizedColumns.extra.map(column => (
                <CardRow
                  item={item}
                  index={index}
                  column={column}
                  key={column.key || `extra-${column.dataIndex}`}
                />
              ))}
            </div>
          )}

          {/* Group 4: Normal Columns (not wrapped in group-columns) */}
          {categorizedColumns.normal.map(column => (
            <CardRow
              item={item}
              index={index}
              column={column}
              key={column.key || `normal-${column.dataIndex}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default CardTable;
